import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import './History.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const History = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [status, setStatus] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingScreenshot, setViewingScreenshot] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isAdmin) {
        // Admin sees all data
        const [activitiesRes, statusRes, totalsRes] = await Promise.all([
          axios.get(`${API_URL}/admin/parsing-activity`),
          axios.get(`${API_URL}/admin/status`),
          axios.get(`${API_URL}/admin/weekly-totals`)
        ]);
        setActivities(activitiesRes.data.activities);
        setStatus(statusRes.data.status);
        setTotals(totalsRes.data.totals);
      } else {
        // Associates see only their own data
        const [activitiesRes, myTimesheetsRes] = await Promise.all([
          axios.get(`${API_URL}/admin/parsing-activity`),
          axios.get(`${API_URL}/upload/my-timesheets`)
        ]);
        setActivities(activitiesRes.data.activities);
        
        // Calculate status from own timesheets
        const myTimesheets = myTimesheetsRes.data.timesheets || [];
        setStatus({
          totalFiles: myTimesheets.length,
          filesInProgress: myTimesheets.filter(ts => ts.status === 'processing').length,
          filesCompleted: myTimesheets.filter(ts => ts.status === 'completed').length,
          filesSubmitted: myTimesheets.filter(ts => ts.status === 'submitted').length,
          filesWithErrors: myTimesheets.filter(ts => ts.status === 'error').length,
          filesPending: myTimesheets.filter(ts => ts.status === 'pending').length
        });
        
        // Calculate own totals
        const totalHours = myTimesheets
          .filter(ts => ts.status === 'submitted')
          .reduce((sum, ts) => sum + (parseFloat(ts.totalHours) || 0), 0);
        setTotals({
          totalHours: totalHours.toFixed(2),
          associatesReported: 1,
          weekOf: 'Current Week'
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'processing':
        return '⏳';
      case 'error':
        return '❌';
      default:
        return '⏸️';
    }
  };

  const handleViewScreenshot = async (activity) => {
    if (!activity.timesheetId) {
      alert('No screenshot available for this activity');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/reports/download-screenshot/${activity.timesheetId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setScreenshotUrl(url);
      setViewingScreenshot(activity);
    } catch (error) {
      console.error('Error loading screenshot:', error);
      alert('Failed to load screenshot');
    }
  };

  const handleCloseScreenshot = () => {
    if (screenshotUrl) {
      window.URL.revokeObjectURL(screenshotUrl);
    }
    setScreenshotUrl(null);
    setViewingScreenshot(null);
  };

  return (
    <Layout>
      <div className="history-page">
        <h1>History</h1>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            <div className="history-grid">
              <div className="history-section">
                <h2>{isAdmin ? 'Recent Parsing Activity' : 'My Recent Uploads'}</h2>
                <table className="activity-table">
                  <thead>
                    <tr>
                      <th>File Name</th>
                      {isAdmin && <th>Uploader</th>}
                      <th>Upload Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 4 : 3} className="no-data">No activity yet</td>
                      </tr>
                    ) : (
                      activities.map((activity, index) => (
                        <tr key={index}>
                          <td>{activity.fileName}</td>
                          {isAdmin && <td>{activity.uploader}</td>}
                          <td>{moment(activity.uploadTime).format('YYYY-MM-DD hh:mm A')}</td>
                          <td>
                            {activity.status === 'error' && activity.errorMessage ? (
                              <span className="status-cell status-error-with-tooltip" title={activity.errorMessage}>
                                {getStatusIcon(activity.status)} {activity.status}
                              </span>
                            ) : (
                              <span className="status-cell">
                                {getStatusIcon(activity.status)} {activity.status}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="status-section">
                <h2>{isAdmin ? 'Overall Processing Status' : 'My Processing Status'}</h2>
                <div className="status-cards">
                  <div className="status-card">
                    <div className="status-label">Total Files Uploaded</div>
                    <div className="status-value">{status?.totalFiles || 0}</div>
                  </div>
                  <div className="status-card">
                    <div className="status-label">Files In Progress</div>
                    <div className="status-value in-progress">
                      {status?.filesInProgress || 0}
                    </div>
                  </div>
                  <div className="status-card">
                    <div className="status-label">Files Completed</div>
                    <div className="status-value completed">
                      {status?.filesCompleted || 0}
                    </div>
                  </div>
                  <div className="status-card">
                    <div className="status-label">Files With Errors</div>
                    <div className="status-value error">
                      {status?.filesWithErrors || 0}
                    </div>
                  </div>
                </div>
              </div>

              <div className="totals-section">
                <h2>{isAdmin ? 'Weekly Time Totals Summary' : 'My Weekly Totals'}</h2>
                <div className="totals-card">
                  <div className="totals-value">{totals?.totalHours || '0.00'} Hours</div>
                  <div className="totals-label">{isAdmin ? 'Total processed this week' : 'Your total hours this week'}</div>
                  {isAdmin && (
                    <div className="totals-details">
                      <div>Associates Reported: {totals?.associatesReported || 0}</div>
                      <div>Week Of: {totals?.weekOf || 'N/A'}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="historical-reports-section">
                <h2>Historical Reports & Screenshots</h2>
                <div className="reports-list">
                  {activities.slice(0, 5).map((activity, index) => (
                    <div 
                      key={index} 
                      className="report-item" 
                      onClick={() => handleViewScreenshot(activity)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="report-info">
                        <h3>{activity.fileName}</h3>
                        <p>Uploaded: {moment(activity.uploadTime).format('YYYY-MM-DD hh:mm A')}</p>
                        <div className="report-thumbnail">📄</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Screenshot Viewer Modal */}
            {viewingScreenshot && (
              <div className="modal-overlay" onClick={handleCloseScreenshot}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '90vh' }}>
                  <div className="modal-header">
                    <h3>{viewingScreenshot.fileName}</h3>
                    <button className="modal-close" onClick={handleCloseScreenshot}>×</button>
                  </div>
                  <div className="modal-body" style={{ overflow: 'auto', maxHeight: 'calc(90vh - 80px)' }}>
                    {screenshotUrl && (
                      <img 
                        src={screenshotUrl} 
                        alt={viewingScreenshot.fileName} 
                        style={{ width: '100%', height: 'auto' }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default History;

